import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "store";
import { Loader } from "components/Loader";
import { PromptOnLeave } from "components/PromptOnLeave";
import { PlatformSelector } from "components/AutodialerSettings/components/PlatformSelector";
import { RepTeamSelector } from "components/AutodialerSettings/components/RepTeamSelector";
import { PlatformHeader } from "components/AutodialerSettings/components/PlatformHeader";
import { PendingChangesBar } from "components/AutodialerSettings/components/PendingChangesBar";
import { SettingsTabsHeader } from "components/AutodialerSettings/components/SettingsTabsHeader";
import { SettingsTabContent } from "components/AutodialerSettings/components/SettingsTabContent";
import { useAutodialerSettings } from "components/AutodialerSettings/hooks/useAutodialerSettings";
import { loadAutodialerSettings } from "lib/redux/settings/autodialerSettingsSlice";
import { SelectionType } from "interfaces/settings";
import { loadAutodialerMappings } from "lib/redux/settings/autodialerMappingsSlice";
import { useUserData } from '../../lib/redux/users/user-data-hook';

export function AutodialerSettingsV2() {
    const dispatch = useDispatch();
    const {
        selectedPlatform,
        hasUnsavedChanges,
        isLoaded,
        error,  
        updateSelection,
        selection,
    } = useAutodialerSettings()

    const { user, userGroupsLoaded: userGroupLoaded, isAdmin } = useUserData();
    
    // Also check if mappings are loaded and have changes
    const mappingsIsLoaded = useSelector((state: RootState) => state.autodialerMappings.isLoaded);
    const mappingsError = useSelector((state: RootState) => state.autodialerMappings.error);
    const hasUnsavedMappings = useSelector((state: RootState) => state.autodialerMappings.hasUnsavedChanges);
    
    const hasAnyUnsavedChanges = hasUnsavedChanges || hasUnsavedMappings;
    
    useEffect(() => {
        if (!isLoaded && !error) loadAutodialerSettings(dispatch);
    }, [dispatch, isLoaded, error])

    useEffect(() => {
        if (!mappingsIsLoaded && !mappingsError) loadAutodialerMappings(dispatch);
    }, [dispatch, mappingsIsLoaded, mappingsError])

    useEffect(() => {   
        if (user && !selection) updateSelection(SelectionType.ME)
    }, [user, updateSelection, selection])
    
    if (!user || isAdmin === null || !userGroupLoaded || !isLoaded || !mappingsIsLoaded) return <Loader />;
    if (!selectedPlatform) return <PlatformSelector />;
    
    return (
        <div className="flex h-full bg-gray-50 min-w-[550px]">
            <PromptOnLeave 
                message="Make sure to save your autodialer settings!" 
                when={hasAnyUnsavedChanges}
            />
            
            {/* Left Sidebar - MADE WIDER */}
            {isAdmin && (
                <div className="w-40 min-w-40 max-w-40 bg-white border-r border-gray-200 flex flex-col h-full flex-shrink-0" style={{ width: '174px', minWidth: '174px', maxWidth: '174px' }}>
                    <div className="flex-1 min-h-0 overflow-auto">
                        <RepTeamSelector />
                    </div>
                </div>
            )}
            
            {/* Main Content */}
            <div className="flex-1 flex flex-col h-full min-w-0">
                {/* Header */}
                <div className="bg-white px-4 py-2 flex-shrink-0">
                    <PlatformHeader />
                </div>
                {/* Pending Changes Bar */}
                <div className="px-4 flex-shrink-0">
                    <PendingChangesBar />
                </div>
                <SettingsTabsHeader />
                {/* Content Area */}
                <div className="flex-1 overflow-auto bg-gray-50 px-4 pb-4 min-h-0">
                    <SettingsTabContent />
                </div>
            </div>
        </div>
    );
}
